"use client";

import { useState, useEffect } from "react";
import { useLang } from "@/context/lang";
import { motion } from "motion/react";
import { ArrowRight, Mic } from "lucide-react";
import Link from "next/link";

export function CTA() {
  const { T, lang } = useLang();
  const [loggedIn, setLoggedIn] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [studioLoading, setStudioLoading] = useState(false);

  useEffect(() => {
    setMounted(true);
    setLoggedIn(!!localStorage.getItem("speakroar-user"));
  }, []);

  const openStudio = async () => {
    setStudioLoading(true);
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || "https://studio.speakroar.com";
    try {
      const resp = await fetch("/api/auth/create-otp", { method: "POST" });
      const data = await resp.json();
      if (data.success && data.token) {
        window.open(`${apiUrl}/auto-login/${data.token}`, "_blank");
      } else {
        window.open(apiUrl, "_blank");
      }
    } catch {
      window.open(apiUrl, "_blank");
    } finally {
      setStudioLoading(false);
    }
  };

  return (
    <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-cyan-500 to-teal-500 overflow-hidden">
      {/* Animated background decoration */}
      <motion.div
        className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-white/10 rounded-full blur-3xl"
        animate={{ x: [0, 50, 0], y: [0, -30, 0], scale: [1, 1.2, 1] }}
        transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-0 right-1/4 w-[300px] h-[300px] bg-teal-300/20 rounded-full blur-3xl"
        animate={{ x: [0, -40, 0], y: [0, 25, 0], scale: [1, 0.9, 1] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="max-w-4xl mx-auto text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <motion.h2
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, type: "spring", stiffness: 100 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight"
          >
            {T("cta.title1")}{" "}
            <span className="text-white/90 underline decoration-white/30 underline-offset-4">
              {T("cta.title2")}
            </span>{" "}
            {T("cta.title3")}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="text-white/80 mt-6 text-lg max-w-2xl mx-auto"
          >
            {T("cta.desc")}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
              {mounted && loggedIn ? (
                <button
                  onClick={openStudio}
                  disabled={studioLoading}
                  className="group px-8 py-4 rounded-full bg-white dark:bg-slate-900 text-cyan-600 dark:text-cyan-300 font-semibold text-base hover:shadow-xl hover:shadow-white/25 transition-all duration-300 flex items-center gap-2 disabled:opacity-60"
                >
                  {studioLoading ? <div className="w-4 h-4 border-2 border-cyan-400/30 border-t-cyan-500 rounded-full animate-spin" /> : <Mic size={18} />}
                  {T("nav.go_studio")}
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
              ) : (
                <Link
                  href="/register"
                  className="group px-8 py-4 rounded-full bg-white dark:bg-slate-900 text-cyan-600 dark:text-cyan-300 font-semibold text-base hover:shadow-xl hover:shadow-white/25 transition-all duration-300 flex items-center gap-2"
                >
                  {T("cta.btn1")}
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              )}
            </motion.div>
            <motion.a
              href="mailto:info@speakroar.com"
              className="px-8 py-4 rounded-full border border-white/30 text-white font-semibold text-base hover:border-white/60 hover:bg-white/10 transition-all duration-200"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
            >
              {T("cta.btn2")}
            </motion.a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
